import { createContext, useContext, useState, useEffect, ReactNode } from "react";

export type UserRole =
  | "super_admin"
  | "owner"
  | "ops_manager"
  | "admin"
  | "warehouse_manager"
  | "installer"
  | "subcontractor"
  | "sales"
  | "sales_manager"
  | "call_center"
  | "cfo"
  | "finance_manager"
  | "bookkeeper"
  | "head_of_installation"
  | "procurement_manager"
  | "inventory_manager"
  | "it_staff";

export interface RoleInfo {
  key: UserRole;
  label: string;
  title: string;
  description: string;
}

export const ROLES: RoleInfo[] = [
  { key: "super_admin", label: "Super Admin", title: "Full Platform Access", description: "All features, all views, full system control" },
  { key: "owner", label: "Owner", title: "Business Overview", description: "Revenue, performance, pipeline, strategic decisions" },
  { key: "cfo", label: "CFO", title: "Financial Command Center", description: "Cash flow, AR, margin protection, financial risk" },
  { key: "finance_manager", label: "Finance Manager", title: "Financial Operations", description: "Receivables, credits, deposits, job financials" },
  { key: "bookkeeper", label: "Bookkeeper", title: "Financial Records", description: "Payment tracking, invoicing, credit records" },
  { key: "ops_manager", label: "Ops Manager", title: "Operations Center", description: "Dispatch, job movement, bottlenecks, crew coordination" },
  { key: "admin", label: "Admin", title: "Admin Console", description: "Permits, ordering, scheduling, notes, documentation" },
  { key: "warehouse_manager", label: "Warehouse", title: "Warehouse Operations", description: "Intake, staging, material readiness, inventory" },
  { key: "head_of_installation", label: "Head of Install", title: "Installation Command", description: "Job readiness, crew scheduling, material flow" },
  { key: "procurement_manager", label: "Procurement", title: "Supply Chain", description: "Purchasing, vendor management, stock levels" },
  { key: "inventory_manager", label: "Inventory", title: "Inventory Control", description: "Stock tracking, allocation, reorder management" },
  { key: "installer", label: "Field Crew", title: "My Assignments", description: "Assigned jobs, install QA forms, issue reporting" },
  { key: "subcontractor", label: "Subcontractor", title: "Contractor Portal", description: "Opportunities, accepted jobs, documentation" },
  { key: "sales", label: "Sales Rep", title: "Sales Pipeline", description: "Sold jobs, issue tracking, customer handoff" },
  { key: "sales_manager", label: "Sales Manager", title: "Sales Oversight", description: "Rep performance, scorecards, appointment analytics" },
  { key: "call_center", label: "Call Center", title: "Lead Operations", description: "Inbound leads, conversations, follow-ups, scheduling" },
  { key: "it_staff", label: "IT Staff", title: "IT Administration", description: "System configuration, settings, and technical administration" },
];

interface RoleContextValue {
  role: UserRole;
  accountRole: UserRole; // the real auth-derived role, never changes with manual switches
  setRole: (role: UserRole) => void;
  roleInfo: RoleInfo;
}

const RoleContext = createContext<RoleContextValue | null>(null);

const isValidRole = (r?: UserRole | null): r is UserRole =>
  !!r && ROLES.some((item) => item.key === r);

const ROLE_ALIASES: Record<string, UserRole> = {
  "super admin": "super_admin",
  super_admin: "super_admin",
  superadmin: "super_admin",
  owner: "owner",
  "ops manager": "ops_manager",
  ops_manager: "ops_manager",
  operations_manager: "ops_manager",
  operations: "ops_manager",
  admin: "admin",
  "warehouse manager": "warehouse_manager",
  warehouse_manager: "warehouse_manager",
  warehouse: "warehouse_manager",
  installer: "installer",
  "field crew": "installer",
  subcontractor: "subcontractor",
  subcontractors: "subcontractor",
  sales: "sales",
  "sales rep": "sales",
  sales_rep: "sales",
  salesperson: "sales",
  "sales manager": "sales_manager",
  sales_manager: "sales_manager",
  "call center": "call_center",
  call_center: "call_center",
  callcenter: "call_center",
  cfo: "cfo",
  "finance manager": "finance_manager",
  finance_manager: "finance_manager",
  bookkeeper: "bookkeeper",
  "head of install": "head_of_installation",
  head_of_installation: "head_of_installation",
  "head of installation": "head_of_installation",
  "procurement manager": "procurement_manager",
  procurement_manager: "procurement_manager",
  procurement: "procurement_manager",
  "inventory manager": "inventory_manager",
  inventory_manager: "inventory_manager",
  inventory: "inventory_manager",
  "it staff": "it_staff",
  it_staff: "it_staff",
};

export function normalizeUserRole(value: string | null | undefined): UserRole | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (isValidRole(trimmed as UserRole)) return trimmed as UserRole;

  const normalizedKey = trimmed
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  return ROLE_ALIASES[normalizedKey]
    ?? ROLE_ALIASES[trimmed.toLowerCase()]
    ?? ROLE_ALIASES[trimmed.toLowerCase().replace(/_/g, " ")]
    ?? null;
}

export function RoleProvider({ children, initialRole }: { children: ReactNode; initialRole?: UserRole }) {
  const normalizedInitialRole = normalizeUserRole(initialRole);
  const seed: UserRole = normalizedInitialRole ?? "sales";
  const [role, setRole] = useState<UserRole>(seed);
  // accountRole = the real auth-derived role. Stored in state so it survives:
  //   1. Manual role switches (setRole doesn't touch it)
  //   2. Transient null re-fetches from the realtime subscription
  const [accountRole, setAccountRole] = useState<UserRole>(seed);
  const roleInfo = ROLES.find((r) => r.key === role)!;

  // Re-sync when the auth-derived role resolves or changes (e.g. slow initial load, real-time update).
  // If initialRole becomes null/invalid (e.g. a failed re-fetch), we deliberately do NOT reset
  // either value — preserve the last known real role.
  useEffect(() => {
    const normalizedRole = normalizeUserRole(initialRole);
    if (normalizedRole) {
      setRole(normalizedRole);
      setAccountRole(normalizedRole);
    }
  }, [initialRole]);

  return (
    <RoleContext.Provider value={{ role, accountRole, setRole, roleInfo }}>
      {children}
    </RoleContext.Provider>
  );
}

export function useRole() {
  const ctx = useContext(RoleContext);
  if (!ctx) throw new Error("useRole must be used within RoleProvider");
  return ctx;
}
